import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { translateValidationDetail } from '../lib/apiErrorMessages';

const API = process.env.REACT_APP_BACKEND_URL;

// LOT 494 — Safari ITP fallback (Daniel/Emilie 15/05/2026) :
// Frontend Vercel ↔ backend Railway = cookies cross-site. Safari ITP les
// bloque silencieusement → après /auth/login OK, la navigation suivante
// renvoie 401 et l'utilisateur retombe sur /login.
//
// Solution : stocker aussi le `access_token` retourné par /auth/login en
// localStorage et l'injecter en `Authorization: Bearer` sur chaque requête
// (le backend accepte déjà ce header en fallback du cookie via
// core.permissions._extract_token). Chrome/Firefox continuent à utiliser
// le cookie HttpOnly (defense in depth). Pas de changement DNS requis.
const TOKEN_STORAGE_KEY = 'wm_access_token';

const safeStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
};
function getStoredToken() {
  const s = safeStorage();
  if (!s) return null;
  try { return s.getItem(TOKEN_STORAGE_KEY); } catch { return null; }
}
function setStoredToken(token) {
  const s = safeStorage();
  if (!s) return;
  try {
    if (token) s.setItem(TOKEN_STORAGE_KEY, token);
    else s.removeItem(TOKEN_STORAGE_KEY);
  } catch { /* localStorage may be disabled (private mode) — silent */ }
}

// Default request timeout (ms). Without this, a hanging request stays pending
// indefinitely and the caller only ever sees a blank loading state — the user
// cannot distinguish « slow » from « broken ». 30s is generous enough for the
// heaviest admin detail endpoints and short enough to surface real issues.
axios.defaults.timeout = 30000;

// LOT 494 — Global request interceptor: inject Bearer header when we have a
// token in storage. Installed once at module load; idempotent on re-imports.
if (!axios.__wm_bearer_interceptor_installed) {
  axios.interceptors.request.use((config) => {
    const t = getStoredToken();
    if (t && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
  });
  axios.__wm_bearer_interceptor_installed = true;
}

const AuthContext = createContext(null);

// Routes that do NOT require an active session — hitting these must never trigger the expired modal.
const PUBLIC_AUTH_PATHS = [
  '/api/auth/login',
  '/api/auth/logout',
  '/api/auth/refresh',
  '/api/auth/me',
  '/api/auth/password-reset',
  '/api/auth/forgot-password',
];

function isPublicAuthRequest(config) {
  const url = config?.url || '';
  return PUBLIC_AUTH_PATHS.some(p => url.includes(p));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({});
  const [adminLevel, setAdminLevel] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  const userRef = useRef(null);
  // Lot 330 · Bloc 7 — timestamp de la dernière authentification réussie.
  //   Permet d'ignorer les 401 parasites qui arrivent dans les 3 s suivant un
  //   login (cookies en cours de propagation sur certains navigateurs mobiles).
  const lastLoginRef = useRef(0);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/my-permissions`, { withCredentials: true });
      setPermissions(res.data.permissions || {});
      setAdminLevel(res.data.admin_level || '');
    } catch {
      setPermissions({});
      setAdminLevel('');
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
      // Fetch permissions if admin
      if (response.data?.role === 'super_admin' || response.data?.role === 'admin') {
        await fetchPermissions();
      }
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Keep a ref of the current user so the axios interceptor can check auth state without stale closures.
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Install a single global axios response interceptor to detect session expiry.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        const status = error?.response?.status;
        const cfg = error?.config || {};
        // Only trigger modal when we had an authenticated user AND the 401 comes from a protected call.
        // Lot 330 · Bloc 7 — grace window : pendant les 3 s suivant un login
        //   réussi, on ignore les 401 (cookie en cours de propagation).
        const withinGrace = Date.now() - (lastLoginRef.current || 0) < 3000;
        if (status === 401 && userRef.current && userRef.current !== false && !isPublicAuthRequest(cfg) && !withinGrace) {
          setSessionExpired(true);
          setUser(false);
          setPermissions({});
          setAdminLevel('');
        }
        return Promise.reject(error);
      }
    );
    return () => { axios.interceptors.response.eject(id); };
  }, []);

  const dismissSessionExpired = useCallback(() => setSessionExpired(false), []);

  // APEX M2 — Finalisation commune post-authentification (login direct,
  // vérification TOTP 2 étapes, ou confirmation d'enrôlement au login) :
  // stockage du token (fallback Safari ITP), hydratation user, probe
  // /auth/me, permissions admin.
  const finalizeAuth = async (data) => {
    // LOT 494 — Stocker le token AVANT tout autre call pour que l'intercepteur
    // axios l'utilise sur la probe /auth/me ci-dessous. Sur Safari ITP, si le
    // cookie est bloqué, ce token est notre seule preuve d'auth pour la suite.
    if (data?.access_token) {
      setStoredToken(data.access_token);
    }
    setUser(data);
    lastLoginRef.current = Date.now();
    // Lot 330 · Bloc 7 — vérification post-login : confirmer que le cookie a
    //   bien été mémorisé par le navigateur (enjeu spécifique iOS Safari /
    //   Android Chrome qui filtrent agressivement les cookies tierce-partie).
    //   On fait un /auth/me juste après pour valider. Si ça échoue, on
    //   remonte une erreur actionable au lieu de laisser l'utilisateur tomber
    //   sur le modal "session expirée" à la navigation suivante.
    try {
      await axios.get(`${API}/api/auth/me`, {
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache' },
        timeout: 5000,
      });
    } catch (probe) {
      if (probe?.response?.status === 401) {
        // LOT 494 — Même le Bearer fallback échoue : on efface le token périmé
        // et on remonte un message clair à l'utilisateur.
        setStoredToken(null);
        setUser(false);
        const err = new Error('cookie-not-stored');
        err.response = {
          data: {
            detail: "Votre navigateur a bloqué le cookie de session. Activez les cookies tiers ou essayez sans navigation privée pour vous connecter.",
          },
        };
        throw err;
      }
      // Autre erreur (timeout, réseau) : on laisse passer, la session est probablement OK.
    }
    // Fetch permissions after login — LOT 521 : on AWAIT pour que la nav
    // suivante (`navigate('/admin')`) ait déjà les permissions hydratées,
    // sinon les PermissionGuard renvoient sur /admin avant que le state
    // soit prêt → page blanche / besoin de refresh manuel (bug Emilie).
    if (data?.role === 'super_admin' || data?.role === 'admin') {
      try {
        await fetchPermissions();
      } catch {
        // Best-effort : si fetch perms échoue (réseau), on garde l'auth
        // et l'utilisateur sera juste limité jusqu'au prochain refresh.
      }
    }
    return data;
  };

  const login = async (email, password, mfaCode = null) => {
    const payload = { email, password };
    if (mfaCode) payload.mfa_code = mfaCode;
    const response = await axios.post(`${API}/api/auth/login`, payload, { withCredentials: true });
    if (response.data.mfa_required || response.data.mfa_setup_required) {
      return response.data; // Caller handles MFA challenge or enrollment redirect
    }
    return finalizeAuth(response.data);
  };

  // APEX M2 — Étape 2 de la connexion : vérification du second facteur
  // (code TOTP ou code de récupération) contre le jeton intermédiaire
  // `mfa_pending` émis par /auth/login. Le JWT n'est émis qu'ici.
  const verifyMfa = async (mfaToken, code, { recovery = false } = {}) => {
    const payload = { mfa_token: mfaToken };
    if (recovery) payload.recovery_code = code;
    else payload.code = code;
    const response = await axios.post(`${API}/api/auth/mfa/verify`, payload, { withCredentials: true });
    if (response.data.force_password_reset) {
      return response.data; // Caller redirects to the reset flow
    }
    return finalizeAuth(response.data);
  };

  // APEX M2 — Fin d'enrôlement dans le flux de login : le backend a
  // finalisé la connexion dans la réponse de /auth/mfa/enroll/confirm.
  const completeAuthFromPayload = async (loginPayload) => finalizeAuth(loginPayload);

  const logout = async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Even if logout fails server-side, clear local auth state.
    }
    // LOT 494 — Effacer le token local quoi qu'il arrive (sinon Bearer
    // continue de fonctionner même après logout côté serveur).
    setStoredToken(null);
    setUser(false);
    setPermissions({});
    setAdminLevel('');
    setSessionExpired(false);
  };

  const refreshToken = async () => {
    try {
      const r = await axios.post(`${API}/api/auth/refresh`, {}, { withCredentials: true });
      // LOT 494 — Update stored token if backend returned one
      if (r.data?.access_token) setStoredToken(r.data.access_token);
      await checkAuth();
    } catch (error) {
      setStoredToken(null);
      setUser(false);
    }
  };

  /** Check if the current admin has a given permission. */
  const canDo = useCallback((perm) => !!permissions[perm], [permissions]);

  const value = {
    user,
    loading,
    login,
    verifyMfa,
    completeAuthFromPayload,
    logout,
    refreshToken,
    checkAuth,
    isAuthenticated: !!user && user !== false,
    isAdmin: user?.role === 'super_admin' || user?.role === 'admin',
    isInvestor: user?.role === 'investor',
    isSponsor: user?.role === 'sponsor',
    // Permission system
    adminLevel,
    permissions,
    canDo,
    // Session expiry surface
    sessionExpired,
    dismissSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// API helper with error handling — Lot 345 : messages humains en français
export function formatApiError(error, lang = 'fr') {
  const detail = error.response?.data?.detail;
  if (detail != null) {
    if (typeof detail === 'string') return detail;
    // SPRINT 8 (recette finale) — 422 Pydantic (tableau/objet) : traduction
    // CANONIQUE en langage maison via src/lib/apiErrorMessages.js. Plus
    // JAMAIS de message de validation anglais brut rendu à l'utilisateur.
    if (Array.isArray(detail)) return translateValidationDetail(detail, lang);
    if (detail && typeof detail.msg === 'string') return translateValidationDetail(detail, lang);
    // LOT 482 — Structured error: {error_code, message}
    if (detail && typeof detail.message === 'string') return detail.message;
    return translateValidationDetail(detail, lang);
  }
  // Lot 345 — Codes HTTP fréquents traduits humainement (pas de jargon technique côté UI)
  const status = error.response?.status;
  const HUMAN_BY_STATUS = {
    400: 'Cette action n\'a pas pu aboutir. Vérifiez les informations saisies.',
    401: 'Votre session a expiré. Veuillez vous reconnecter.',
    403: 'Vous n\'avez pas les droits nécessaires pour cette action.',
    404: 'L\'élément demandé est introuvable.',
    409: 'Un conflit empêche l\'opération (élément déjà existant ou modifié entretemps).',
    410: 'Cette fonctionnalité a été remplacée. Rafraîchissez la page si elle persiste.',
    422: 'Les données envoyées sont incomplètes ou invalides.',
    429: 'Trop de requêtes en peu de temps. Patientez un instant et réessayez.',
    500: 'Une erreur côté serveur est survenue. Si le problème persiste, contactez le support.',
    502: 'Service momentanément indisponible. Réessayez dans quelques instants.',
    503: 'Service momentanément indisponible. Réessayez dans quelques instants.',
    504: 'Service momentanément indisponible. Réessayez dans quelques instants.',
  };
  if (status && HUMAN_BY_STATUS[status]) return HUMAN_BY_STATUS[status];

  // Réseau / pas de réponse du tout
  const code = error.code || '';
  if (code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
    return 'Délai de connexion dépassé. Vérifiez votre réseau et réessayez.';
  }
  if (code === 'ERR_NETWORK' || /Network Error/i.test(error.message || '')) {
    return 'Connexion impossible au service. Vérifiez votre réseau.';
  }
  if (status) {
    const statusText = error.response?.statusText || '';
    return `Erreur ${status}${statusText ? ' — ' + statusText : ''}. Veuillez réessayer.`;
  }
  return 'Une erreur inattendue est survenue. Veuillez réessayer.';
}
